import {Component, Input} from '@angular/core';
import {CompanyResponse, EventResponse, OrderResponse, CoOrganisedEventResponse} from '../../../../@shared/models/response.module';
import {EventsService} from '../../../../@shared/services/events-service';
import {PaymentService} from '../../../../@shared/services/payment.service';
import {AQH_EVE_0} from '../../../../@shared/models/input.module';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AddEvent} from './components/add-event/add-event';
import {DeleteModal} from '../../../../@shared/components/delete-modal/delete-modal';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '../../../../@core/services/toast.service';

export type EventFilter = 'all' | 'upcoming' | 'past' | 'co-organised' | 'cancelled';

@Component({
  selector: 'app-company-profile-events',
  standalone: false,
  templateUrl: './company-profile-events.html',
  styleUrl: './company-profile-events.css'
})
export class CompanyProfileEvents {
  @Input() company: CompanyResponse
  events: EventResponse[] = [];
  allEvents: EventResponse[] = [];
  coOrganisedEvents: CoOrganisedEventResponse[] = [];
  loading: boolean = false;
  loadingCoOrganised: boolean = false;
  deleting: boolean = false;
  selectedFilter: EventFilter = 'all';


  constructor(private eventService: EventsService,
              private paymentService: PaymentService,
              private modalService: NgbModal,
              private router: Router,
              private translateService: TranslateService,
              private toastService: ToastService) {
  }


  ngOnInit() {
    this.getCompanyEvents();
    this.getCoOrganisedEvents();
  }

  getCompanyEvents() {
    this.loading = true;
    let payload: AQH_EVE_0 = {
      company_id: this.company.company_id
    }
    this.eventService.getEvents(payload).subscribe({
      next: (res) => {
        this.allEvents = res.data;
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.allEvents = [];
        this.events = [];
        this.loading = false;
      }
    });
  }

  onFilterChange(filter: EventFilter) {
    this.selectedFilter = filter;
    this.applyFilter();
  }

  applyFilter() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get non-cancelled events for main list
    const activeEvents = this.allEvents.filter(e => e.status !== 'cancelled');

    switch (this.selectedFilter) {
      case 'upcoming':
        this.events = activeEvents.filter(event => {
          if (!event.start_date) return false;
          return new Date(event.start_date) >= today;
        });
        break;
      case 'past':
        this.events = activeEvents.filter(event => {
          if (!event.start_date) return true;
          return new Date(event.start_date) < today;
        });
        break;
      case 'cancelled':
        // Main list empty, cancelled shown in separate section
        this.events = [];
        break;
      case 'co-organised':
        // Co-organised events are shown separately, so empty the main list
        this.events = [];
        break;
      case 'all':
      default:
        this.events = [...activeEvents];
        break;
    }
  }

  get cancelledEvents(): EventResponse[] {
    return this.allEvents.filter(e => e.status === 'cancelled');
  }

  get showCancelledSection(): boolean {
    return (this.selectedFilter === 'all' || this.selectedFilter === 'cancelled') && this.cancelledEvents.length > 0;
  }

  getFilterLabel(filter: EventFilter): string {
    switch (filter) {
      case 'all': return this.translateService.instant('companyProfile.events.filters.all');
      case 'upcoming': return this.translateService.instant('companyProfile.events.filters.upcoming');
      case 'past': return this.translateService.instant('companyProfile.events.filters.past');
      case 'co-organised': return this.translateService.instant('companyProfile.events.filters.coOrganised');
      case 'cancelled': return this.translateService.instant('companyProfile.events.filters.cancelled');
      default: return filter;
    }
  }

  getFilterCount(filter: EventFilter): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'upcoming':
        return this.allEvents.filter(e => e.status !== 'cancelled' && e.start_date && new Date(e.start_date) >= today).length;
      case 'past':
        return this.allEvents.filter(e => e.status !== 'cancelled' && (!e.start_date || new Date(e.start_date) < today)).length;
      case 'cancelled':
        return this.allEvents.filter(e => e.status === 'cancelled').length;
      case 'co-organised':
        return this.coOrganisedEvents.length;
      case 'all':
      default:
        return this.allEvents.length;
    }
  }

  getCoOrganisedEvents() {
    this.loadingCoOrganised = true;
    this.eventService.getCoOrganisedEvents().subscribe({
      next: (res: CoOrganisedEventResponse[]) => {
        this.coOrganisedEvents = res;
        this.loadingCoOrganised = false;
      },
      error: () => {
        this.coOrganisedEvents = [];
        this.loadingCoOrganised = false;
      }
    });
  }

  getAccessLevelBadgeClass(level: string): string {
    switch (level) {
      case 'full': return 'badge-light-success';
      case 'limited': return 'badge-light-warning';
      case 'view': return 'badge-light-info';
      default: return 'badge-light-secondary';
    }
  }

  openAddEvent() {
    // Navigate to the wizard with company ID
    this.router.navigate(['company-profile/create-event', this.company.company_id]);
  }

  openQuickAddEvent() {
    // Keep the old simple add event for quick creation
    this.router.navigate(['company-profile/add-event']);
  }

  onDeleteEvent(event: EventResponse) {
    this.deleting = true;

    // First check for orders
    this.paymentService.getOrders({ event_id: event.event_id }).subscribe({
      next: (orders: OrderResponse[]) => {
        // Find orders that are not refunded or cancelled
        const activeOrders = orders.filter(order =>
          order.status !== 'refunded' && order.status !== 'cancelled'
        );

        if (activeOrders.length > 0) {
          // Has active orders - show error modal
          this.showCannotDeleteModal(event, activeOrders);
        } else {
          // No active orders - confirm deletion
          this.showConfirmDeleteModal(event, orders.length);
        }
      },
      error: () => {
        // If we can't fetch orders, still allow deletion with warning
        this.showConfirmDeleteModal(event, 0);
      }
    });
  }

  private showCannotDeleteModal(event: EventResponse, activeOrders: OrderResponse[]) {
    const paidOrders = activeOrders.filter(o => o.status === 'paid').length;
    const pendingOrders = activeOrders.filter(o => o.status === 'pending').length;

    let message = `This event has orders that must be refunded or cancelled first:\n\n`;
    if (paidOrders > 0) {
      message += `• ${paidOrders} paid order${paidOrders > 1 ? 's' : ''} (require refund)\n`;
    }
    if (pendingOrders > 0) {
      message += `• ${pendingOrders} pending order${pendingOrders > 1 ? 's' : ''} (require cancellation)\n`;
    }
    message += `\nPlease handle these orders in the "Payment & Orders" tab before deleting.`;

    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.title = `Cannot Delete Event`;
    modalRef.componentInstance.description = message;
    modalRef.componentInstance.isError = true;

    modalRef.result.finally(() => {
      this.deleting = false;
    });
  }

  private showConfirmDeleteModal(event: EventResponse, totalOrders: number) {
    let message = `Are you sure you want to delete "${event.event_name}"?\n\n`;
    if (totalOrders > 0) {
      message += `This event has ${totalOrders} refunded/cancelled order${totalOrders > 1 ? 's' : ''} in its history.\n\n`;
    }
    message += `This action cannot be undone.`;

    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.title = 'Delete Event';
    modalRef.componentInstance.description = message;
    modalRef.componentInstance.confirmLabel = 'Delete Event';

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.eventService.deleteEvent({ event_id: event.event_id }).subscribe({
          next: () => {
            this.deleting = false;
            this.getCompanyEvents();
          },
          error: () => {
            this.showErrorModal('Failed to delete event. Please try again.');
            this.deleting = false;
          }
        });
      } else {
        this.deleting = false;
      }
    }).catch(() => {
      this.deleting = false;
    });
  }

  private showErrorModal(message: string) {
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.title = 'Error';
    modalRef.componentInstance.description = message;
    modalRef.componentInstance.isError = true;
  }
}
